import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { AppTileBroadcastMessages } from '@app/models/message-broadcast-app-tile';
import { UserAppTile } from '@app/models/user-app-tile';
import { UserService } from '@app/services/user.service';
import { isAfter, isBefore } from 'date-fns/esm';
import { BehaviorSubject, concat, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class UserAppTileService {

  constructor(private http: HttpClient, private cache: CacheService, private userService: UserService, private httpHelper: HttpHelper) { }

  public readonly cacheKeyPrefix = 'UserAppTileService-getUserAppTiles';
  public useCache = true;
  private getUserAppTiles$ = new Map<string, Observable<Array<UserAppTile>>>();

  private getNewOrUpdatedUserAppTilesSinceLastVisit$: Observable<Array<UserAppTile>>;

  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  public reload() {
    this.getUserAppTiles$.clear();
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKeyPrefix);
    }
    this.reload$.next();
    this.refresh$.next(null);
  }

  /*
   * Returns user app tiles that the current user is licensed to use
   */
  getLicensedUserAppTiles(includeHidden = false): Observable<Array<UserAppTile>> {
    const userAppTiles$ = this.getUserAppTiles(includeHidden);
    const getMyDetailsFromMsGraph$ = this.userService.getMyDetailsFromMsGraph();
    return forkJoin(
      [
        userAppTiles$,
        getMyDetailsFromMsGraph$
      ])
      .pipe(
        map(([userAppTiles, userDetails]) => {
          // remove apps that the user does not have a license to user
          userAppTiles = userAppTiles.filter(app => {
            if (!app.AppTile.O365ServiceName || app.AppTile.O365ServiceName === '') {
              // no license required
              return true;
            }
            const foundLicense = userDetails.assignedPlans.find(plan => {
              return plan.service.toLowerCase() === app.AppTile.O365ServiceName.toLowerCase();
            });
            return foundLicense !== undefined && foundLicense.capabilityStatus === 'Enabled';
          });
          return userAppTiles;
        }),
        catchError(this.httpHelper.handleError({ operation: 'getLicensedUserAppTiles', result: ({} as UserAppTile[]), rethrow: true }))
      );
  }

  getNewOrUpdatedUserAppTilesSinceLastVisit(lastVisitedDate: Date): Observable<Array<UserAppTile>> {
    if (this.getNewOrUpdatedUserAppTilesSinceLastVisit$) {
      return this.getNewOrUpdatedUserAppTilesSinceLastVisit$;
    } else {
      const httpParams = new HttpParams().append('lastVisitedDate', `${lastVisitedDate.toISOString()}`);

      this.getNewOrUpdatedUserAppTilesSinceLastVisit$ = this.http.get<Array<UserAppTile>>(`${Constants.API_ENDPOINT}/me/userapptiles/updates`, {
        params: httpParams
      })
        .pipe(
          map(response => {
            console.log(`fetched result getNewOrUpdatedUserAppTilesSinceLastVisit:`, response);
            return response.map(p => {
              const userAppTile = new UserAppTile(p);
              // filter AppTileBroadcastMessages to get only active messages on app tile.
              if (userAppTile.AppTile && userAppTile.AppTile.AppTileBroadcastMessages.length > 0) {
                const activeAptileBroadcastMessages: AppTileBroadcastMessages[] = userAppTile.AppTile.AppTileBroadcastMessages.filter(b => (!b.BroadcastMessage.EndDate || isAfter(new Date(b.BroadcastMessage.EndDate), new Date())) && (!b.BroadcastMessage.StartDate || isBefore(new Date(b.BroadcastMessage.StartDate), new Date())));
                userAppTile.AppTile.AppTileBroadcastMessages = activeAptileBroadcastMessages;
              }
              return userAppTile;
            });
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNewOrUpdatedUserAppTilesSinceLastVisit', result: new Array<UserAppTile>(), rethrow: true })),
          shareReplay({ bufferSize: 1, refCount: true }));
      return this.getNewOrUpdatedUserAppTilesSinceLastVisit$;
    }
  }

  getUserAppTiles(includeHidden = false): Observable<Array<UserAppTile>> {
    if (this.getUserAppTiles$.has(includeHidden.toString())) {
      return this.getUserAppTiles$.get(includeHidden.toString());
    } else {
      const httpParams = new HttpParams().set('includeHidden', `${includeHidden}`);
      const cacheKey = `${this.cacheKeyPrefix}-${includeHidden}`;

      this.getUserAppTiles$.set(includeHidden.toString(), this.http.get<Array<UserAppTile>>(`${Constants.API_ENDPOINT}/me/userapptiles`, {
        params: httpParams
      })
        .pipe(
          map(response => {
            console.log(`fetched result getUserAppTiles: `, response);

            return response.map(p => {
              const userAppTile = new UserAppTile(p);
              // filter AppTileBroadcastMessages to get only active messages on app tile.
              if (userAppTile.AppTile && userAppTile.AppTile.AppTileBroadcastMessages.length > 0) {
                const activeAptileBroadcastMessages: AppTileBroadcastMessages[] = userAppTile.AppTile.AppTileBroadcastMessages.filter(b => (!b.BroadcastMessage.EndDate || isAfter(new Date(b.BroadcastMessage.EndDate), new Date())) && (!b.BroadcastMessage.StartDate || isBefore(new Date(b.BroadcastMessage.StartDate), new Date())));
                userAppTile.AppTile.AppTileBroadcastMessages = activeAptileBroadcastMessages;
              }
              return userAppTile;
            });
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getUserAppTiles', result: new Array<UserAppTile>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<UserAppTile>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getUserAppTiles$.get(includeHidden.toString()));
        }
      }

      return this.getUserAppTiles$.get(includeHidden.toString());
    }
  }

  getUserAppTile(id: number): Observable<UserAppTile> {
    return this.http.get<UserAppTile>(`${Constants.API_ENDPOINT}/me/userapptiles/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result getUserAppTile: `, response);
          return new UserAppTile(response);
        }));
  }

  updateUserAppTile(id: number, userAppTile: UserAppTile): Observable<UserAppTile> {
    const fetchOperation = this.http.put<UserAppTile>(`${Constants.API_ENDPOINT}/me/userapptiles/${id}`, userAppTile, {

    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return userAppTile;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateUserAppTile', result: new UserAppTile(), rethrow: true })));

    return fetchOperation;
  }

  updateUserAppTiles(userAppTiles: Array<UserAppTile>): Observable<Array<UserAppTile>> {
    const fetchOperation = this.http.put<Array<UserAppTile>>(`${Constants.API_ENDPOINT}/me/userapptiles`, userAppTiles)
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return userAppTiles;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateUserAppTiles', result: ({} as UserAppTile[]), rethrow: true })));

    return fetchOperation;
  }

  deleteUserAppTile(id: number): Observable<UserAppTile> {
    const fetchOperation = this.http.delete<UserAppTile>(`${Constants.API_ENDPOINT}/me/userapptiles/${id}`)
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteUserAppTile', result: new UserAppTile(), rethrow: true })));

    return fetchOperation;
  }

  addUserAppTile(userAppTile: UserAppTile): Observable<UserAppTile> {
    const fetchOperation = this.http.post<UserAppTile>(`${Constants.API_ENDPOINT}/me/userapptiles`, userAppTile)
      .pipe(
        map(response => {
          console.log(`added result: `);
          console.log(response);
          this.reload();
          return new UserAppTile(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'addUserAppTile', result: new UserAppTile(), rethrow: true })));

    return fetchOperation;
  }
}
